import {defineStore} from "pinia";
import {ANNOUNCEMENTS} from "@/config/announcements";
import {AnnouncementAudienceEnum} from "@/models/enum/AnnouncementAudienceEnum";
import {AnnouncementModel} from "@/models/announcement/AnnouncementModel";
import {AuthContext} from "@/context/AuthContext";
import {LocalStorageKeyEnum} from "@/models/enum/LocalStorageKeyEnum";
import {SessionStorageKeyEnum} from "@/models/enum/SessionStorageKeyEnum";
import {Storage} from "@/models/facade/Storage";
import {StorageInterface} from "@/models/facade/storage/StorageInterface";

/**
 * Storage key used when there is no user to attribute a dismissal to, i.e. a visitor that is not
 * logged in. A dismissal stored under this key counts for every user of the browser.
 */
const ALL_USERS_KEY = '*';

/** Dismissed announcement codes, per user id. Pharmacy workstations are often shared. */
type DismissedByUser = Record<string, string[]>;

interface State {
    active_announcement: AnnouncementModel | null,
}

export const useAnnouncementStore = defineStore("AnnouncementStore", {
    state: (): State => {
        return {
            active_announcement: null,
        }
    },
    actions: {
        /**
         * Determines which announcement (if any) should be shown to the current user.
         * Safe to call multiple times.
         */
        resolveActiveAnnouncement(): void {
            this.pruneDismissed();

            this.active_announcement = ANNOUNCEMENTS.find((announcement: AnnouncementModel) => {
                return this.isVisibleForAudience(announcement)
                    && this.isWithinPeriod(announcement)
                    && !this.getDismissedCodes(Storage.local, LocalStorageKeyEnum.DISMISSED_ANNOUNCEMENTS).includes(announcement.code)
                    && !this.getDismissedCodes(Storage.session, SessionStorageKeyEnum.DISMISSED_ANNOUNCEMENTS).includes(announcement.code);
            }) ?? null;
        },
        /** Hides the announcement for the current session only, it returns on a next login. */
        dismissForSession(code: string): void {
            this.addDismissedCode(Storage.session, SessionStorageKeyEnum.DISMISSED_ANNOUNCEMENTS, code);
            this.resolveActiveAnnouncement();
        },
        /** Hides the announcement permanently for the current user in this browser. */
        dismissPermanently(code: string): void {
            this.addDismissedCode(Storage.local, LocalStorageKeyEnum.DISMISSED_ANNOUNCEMENTS, code);
            this.resolveActiveAnnouncement();
        },
        isVisibleForAudience(announcement: AnnouncementModel): boolean {
            if (announcement.audience === AnnouncementAudienceEnum.AUTHENTICATED) {
                return AuthContext.isAuthenticated();
            }

            return true;
        },
        isWithinPeriod(announcement: AnnouncementModel): boolean {
            const now = new Date();

            if (announcement.startDate && now < new Date(announcement.startDate + 'T00:00:00')) {
                return false;
            }

            return now <= new Date(announcement.endDate + 'T23:59:59');
        },
        /** Drops stored codes of announcements that no longer exist, to keep the stored value small. */
        pruneDismissed(): void {
            const existingCodes = ANNOUNCEMENTS.map((announcement: AnnouncementModel) => announcement.code);

            [
                {storage: Storage.local, key: LocalStorageKeyEnum.DISMISSED_ANNOUNCEMENTS},
                {storage: Storage.session, key: SessionStorageKeyEnum.DISMISSED_ANNOUNCEMENTS},
            ].forEach(({storage, key}) => {
                const dismissed = this.getAllDismissed(storage, key);
                const pruned = {} as DismissedByUser;

                Object.keys(dismissed).forEach((userId: string) => {
                    const codes = dismissed[userId].filter((code: string) => existingCodes.includes(code));

                    if (codes.length > 0) {
                        pruned[userId] = codes;
                    }
                });

                storage.set(key, JSON.stringify(pruned));
            });
        },
        getAllDismissed(storage: StorageInterface, key: LocalStorageKeyEnum | SessionStorageKeyEnum): DismissedByUser {
            try {
                return JSON.parse(storage.get(key, '{}')) as DismissedByUser;
            } catch (error: any) {
                return {};
            }
        },
        /** Codes dismissed by the current user, plus the ones dismissed while nobody was logged in. */
        getDismissedCodes(storage: StorageInterface, key: LocalStorageKeyEnum | SessionStorageKeyEnum): string[] {
            const dismissed = this.getAllDismissed(storage, key);
            const userId = this.getCurrentUserId();

            if (userId === ALL_USERS_KEY) {
                return dismissed[ALL_USERS_KEY] ?? [];
            }

            return [...(dismissed[userId] ?? []), ...(dismissed[ALL_USERS_KEY] ?? [])];
        },
        addDismissedCode(storage: StorageInterface, key: LocalStorageKeyEnum | SessionStorageKeyEnum, code: string): void {
            const userId = this.getCurrentUserId();
            const dismissed = this.getAllDismissed(storage, key);
            const codes = dismissed[userId] ?? [];

            if (!codes.includes(code)) {
                codes.push(code);
            }
            dismissed[userId] = codes;

            storage.set(key, JSON.stringify(dismissed));
        },
        /** The current user id, or ALL_USERS_KEY when there is nobody logged in. */
        getCurrentUserId(): string {
            try {
                return AuthContext.getUserId() ?? ALL_USERS_KEY;
            } catch (error: any) {
                return ALL_USERS_KEY;
            }
        },
    },
    getters: {
        getActiveAnnouncement(state: State): AnnouncementModel | null {
            return state.active_announcement;
        },
    },
});
